import { useCallback, useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0TableOfContent, TOCItem } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'

export type selectedElementData = {
  index: number
  sectionIndex: number | undefined
}

interface SidebarFormProps {
  form: Form
  onFormUpdated?: () => void
  selectedElement: selectedElementData | undefined
  onSelectElement: (index: number | undefined, sectionIndex: number | undefined) => void
  onAddElement: () => void
}

const updateElementPositions = (form: Form) => {
  form.elements.forEach((el, i) => {
    el.position = i
    if (el instanceof SectionElement) {
      el.elements.forEach((child, childIndex) => {
        child.position = childIndex
      })
    }
  })
}

const SidebarForm = ({
  form,
  onFormUpdated,
  selectedElement,
  onSelectElement,
  onAddElement,
}: SidebarFormProps) => {
  const tocItems = form.elements.map((element, index) => {
    const tocItem: TOCItem = {
      id: element.uuid,
      label: element.label, //TODO this is provisional, until we modify the component
      icon: element.getIcon(),
      onClick: () => onSelectElement(index, undefined),
    }

    // Add children for sections
    if (element instanceof SectionElement && element.elements.length > 0) {
      tocItem.children = element.elements.map((child, childIndex) => ({
        id: child.uuid,
        label: child.label, //TODO this is provisional, until we modify the component
        icon: child.getIcon(),
        onClick: () => onSelectElement(childIndex, index),
      }))
    }

    return tocItem
  })

  const activeItemId = useMemo(() => {
    if (selectedElement === undefined) return undefined

    // Find the actual element to get its uuid
    let element: Element | undefined
    if (selectedElement.sectionIndex !== undefined) {
      const section = form.elements[selectedElement.sectionIndex]
      if (section instanceof SectionElement) {
        element = section.elements[selectedElement.index]
      }
    } else {
      element = form.elements[selectedElement.index]
    }

    if (!element) return undefined
    return element.uuid
  }, [selectedElement, form.elements])

  const onReorder = useCallback(
    (reorderedIds: { id: string; children?: { id: string }[] }[]) => {
      // Build a map of uuid -> element
      const elementMap = new Map<string, Element>()
      form.elements.forEach((el) => {
        elementMap.set(el.uuid, el)
        if (el instanceof SectionElement) {
          el.elements.forEach((child) => {
            elementMap.set(child.uuid, child)
          })
        }
      })

      // newOrder will contain reordered elements not locked
      const newOrder: Element[] = []
      reorderedIds.forEach((idStructure) => {
        const element = elementMap.get(idStructure.id)

        if (!element || element.isLocked()) {
          return
        }

        if (element instanceof SectionElement && idStructure.children) {
          const children: Element[] = []
          for (const childId of idStructure.children) {
            const child = elementMap.get(childId.id)
            if (child) {
              children.push(child)
            }
          }
          element.elements = children
        }

        newOrder.push(element)
      })

      const firstLockedIndex = form.elements.findIndex((el) => el.isLocked())
      const locked = firstLockedIndex === -1 ? [] : form.elements.slice(firstLockedIndex)

      form.elements.length = 0
      form.elements.push(...newOrder, ...locked)
      updateElementPositions(form)
      onFormUpdated?.()
    },
    [form, onFormUpdated]
  )

  return (
    <Box flexDirection="column" alignItems="flexStart">
      <F0TableOfContent
        title={i18n.t('forms.sidebar_title')}
        items={tocItems}
        activeItem={activeItemId}
        sortable
        onReorder={onReorder}
      />
      <F0Button
        icon={Add}
        label={i18n.t('forms.modals.actions.add_content')}
        variant="ghost"
        size="md"
        className="bg-f1-background-selected hover:bg-f1-background-hover"
        onClick={() => onAddElement()}
      />
    </Box>
  )
}

export default SidebarForm
